import { useGLTF } from '@react-three/drei';
import * as THREE from 'three';
import { useEffect, useRef } from 'react';

// Shared model hook to prevent multiple loads and WebGL context issues
export const useSharedBenModel = (modelPath: string = '/benRGB.glb') => {
  const { scene } = useGLTF(modelPath);
  const originalMaterials = useRef<Map<string, Map<THREE.Mesh, THREE.Material | THREE.Material[]>>>(new Map());

  useEffect(() => {
    // Store and fix original materials only once per model
    if (!originalMaterials.current.has(modelPath)) {
      const modelMaterials = new Map<THREE.Mesh, THREE.Material | THREE.Material[]>();
      originalMaterials.current.set(modelPath, modelMaterials);

      scene.traverse((child) => {
        if (child instanceof THREE.Mesh) {
          modelMaterials.set(child, child.material);

          if (child.material) {
            const materials = Array.isArray(child.material) ? child.material : [child.material];
            materials.forEach((material: any) => {
              if (material) {
                material.transparent = false;
                material.opacity = 1.0;
                material.alphaTest = 0.5;
                material.side = THREE.DoubleSide;
                material.depthWrite = true;
                material.depthTest = true;
                material.needsUpdate = true;

                if ('map' in material && material.map) {
                  material.map.flipY = false;
                  material.map.needsUpdate = true;
                }
              }
            });
          }
        }
      });
    }
  }, [scene, modelPath]);

  // Calculate the geometry center for proper rotation
  const clonedScene = scene.clone();
  const box = new THREE.Box3().setFromObject(clonedScene);
  const center = box.getCenter(new THREE.Vector3());

  return {
    scene: clonedScene,
    originalMaterials: originalMaterials.current.get(modelPath) || new Map(),
    geometryCenter: center
  };
};

// Preload both models to avoid loading issues
useGLTF.preload('/benRGB.glb');
useGLTF.preload('/ben.glb');